import { Map } from 'immutable'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { environment } from '../../../environments/environment'
import { Observable, concatMap } from 'rxjs'

@Injectable()
export class PropertiesService {
  constructor(private http: HttpClient) {}

  public fetchProperties(type: string, params: object): Observable<any> {
    let url = `${environment.API_ENDPOINT}/property/list${
      type ? `/${type.toLocaleUpperCase()}` : ''
    }`

    if (type === 'archived') {
      url = `${environment.API_ENDPOINT}/property/archiveList`
    }

    return this.http.post(url, params)
  }

  public generatePropertyRDNs(id: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}/generateRDNs`
    return this.http.get(url, {})
  }

  public generatePropertyCommercialLicenses(id: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}/generateCommercialLicenses`
    return this.http.get(url, {})
  }

  public uploadDocument(id: any, payload: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/document/propertyDocument/${id}`
    return this.http.put(url, payload)
  }

  public deleteDocument(id: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/document/propertyDocument/${id}`
    return this.http.delete(url)
  }

  public deletePayment({ id, payable }: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/payment/${id}`

    return this.http
      .delete(url)
      .pipe(
        concatMap(() =>
          this.http.get(
            `${environment.API_ENDPOINT}/payment/payable/${payable.id}`,
          ),
        ),
      )
  }

  public fetchProperty(id: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}`
    return this.http.get(url, {})
  }

  public fetchPropertyBuilding(id: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}/building`
    return this.http.get(url, {})
  }

  public updateProperty(
    id: number,
    payload: object,
    nextStatus: string | undefined | null,
  ): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}${
      nextStatus ? `/to/${nextStatus.toLocaleUpperCase()}` : ''
    }`
    return this.http.put(url, payload)
  }

  public archiveProperty(id: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}/sendToArchive`
    return this.http.put(url, {})
  }

  public uploadPropertyImage(id: number, payload: object): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}/uploadImage`
    return this.http.post(url, payload)
  }

  public deletePropertyImage(id: number, imageTypeId: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}/deleteImage/${imageTypeId}`
    return this.http.delete(url)
  }

  public createProperty(payload: object): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property`
    return this.http.post(url, payload)
  }

  public fetchActiveForm(): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/activeForm`
    return this.http.get(url, {})
  }

  public fetchPropertySettings(): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/settings`
    return this.http.get(url, {})
  }

  public createPropertyPayment({
    payableId,
    ...payload
  }: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/payment/${payableId}`
    return this.http
      .post(url, payload)
      .pipe(
        concatMap(() =>
          this.http.get(
            `${environment.API_ENDPOINT}/payment/payable/${payableId}`,
          ),
        ),
      )
  }

  public updatePropertyPayment({
    id,
    payableId,
    ...payload
  }: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/payment/${id}`
    return this.http
      .put(url, payload)
      .pipe(
        concatMap(() =>
          this.http.get(
            `${environment.API_ENDPOINT}/payment/payable/${payableId}`,
          ),
        ),
      )
  }

  public calculateFeatures({ parent, ...payload }: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/calculateFeatures`
    return this.http.post(url, payload)
  }

  public downloadPropertyDocument({
    propertyId,
    dataCycleId,
    propertyType,
    id = null,
  }): Observable<any> {
    let url = `${environment.API_ENDPOINT}/property/${propertyId}/downloadRDN/${propertyType}/${dataCycleId}`

    if (!dataCycleId && !propertyType) {
      url = `${environment.API_ENDPOINT}/document/propertyDocument/${id}`
    }

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob' as 'json',
    })
  }

  public generatePropertyDocument({
    propertyId,
    propertyType,
  }): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${propertyId}/generateRDN/${propertyType}`
    return this.http.get(url, {})
  }

  public fetchPropertyPayments(id: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}/financial`
    return this.http.get(url, {})
  }

  public fetchPropertyHistory(id: number, payload: any): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}/history`
    return this.http.post(url, payload)
  }

  public downloadPaymentReceipt(payment: any): Observable<any> {
    let url = `${environment.API_ENDPOINT}/payment/receipt/${payment.id}`

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob' as 'json',
    })
  }

  public generatePaymentReport(propertyId: number): Observable<any> {
    let url = `${environment.API_ENDPOINT}/report/propertyPayments/${propertyId}`

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob' as 'json',
    })
  }

  public generatePaymentTaxpayerReport(propertyId: number): Observable<any> {
    let url = `${environment.API_ENDPOINT}/report/taxpayerReport/${propertyId}`

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob' as 'json',
    })
  }

  public generateBusinessLicenceReport(propertyId: number): Observable<any> {
    let url = `${environment.API_ENDPOINT}/report/businessLicenseReport/${propertyId}`

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob' as 'json',
    })
  }

  public generatePropertyReport(
    propertyId: number,
    propertyType: string,
  ): Observable<any> {
    let url = `${environment.API_ENDPOINT}/report/propertyEvaluationReport/${propertyId}/${propertyType}`

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob' as 'json',
    })
  }

  public generatePropertyBusinessReport(businessId: number): Observable<any> {
    let url = `${environment.API_ENDPOINT}/report/businessEvaluationReport/${businessId}`

    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob' as 'json',
    })
  }

  public setPropertyFieldVisit(
    appealId: number,
    payload: object,
  ): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/fieldVisit/${appealId}`
    return this.http.post(url, payload)
  }

  public fetchPropertyDocuments(id: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}/documents`
    return this.http.get(url, {})
  }

  public fetchPropertyDynamicFields(): Observable<any> {
    const url = `${environment.API_ENDPOINT}/list/calculationDynamicFields`
    return this.http.post(url, {})
  }

  public fetchPropertyBusinesses(id: number): Observable<any> {
    const url = `${environment.API_ENDPOINT}/property/${id}/businesses`
    return this.http.get(url, {})
  }
}
